import { Link, usePathname, useRouter } from "@shared/client/navigation";
import { useEffect, useState } from "react";
import { BarChart3, Building2, Crosshair, Eye, LayoutDashboard, LogOut, Settings, ShieldCheck, Target } from "lucide-react";
import { OfferSwitcher } from "@/components/layout/offer-switcher";
import { RunIndicator, type RunningPassView } from "@/components/layout/run-indicator";
import { CommandPalette } from "@/components/layout/command-palette";
import { resetLeadTasks } from "@/components/lead-tasks";
import { ProductTour } from "@/components/tour/product-tour";
import { Placeholder, RetryButton, useDelayed } from "@/components/page-states";
import type { OfferSummary } from "@shared/lib/offers-shared";
import type { PlanId } from "@shared/lib/billing-shared";
import { buttonVariants } from "@shared/components/ui/button";
import { signOut as endSession } from "@shared/lib/auth-client";
import { Avatar, AvatarFallback } from "@shared/components/ui/avatar";
import { Badge } from "@shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { TooltipProvider } from "@shared/components/ui/tooltip";
import { cn } from "@shared/lib/utils";
import { ADMIN_URL } from "@shared/lib/hosts";
import { setTabStatus } from "@shared/client/tab-status";
import { useT } from "@shared/client/locale";
import type { Messages } from "@shared/i18n";

/**
 * Le rail ne porte que des lieux. « Nouvelle analyse » y figurait comme une
 * entrée de navigation pointant vers une ancre du tableau de bord : une action
 * déguisée en destination, jamais active, et doublon de « Vue d’ensemble ».
 * L’action vit désormais dans la carte de veille, là où son état est connu.
 */
const navigation: Array<{ href: string; label: keyof Messages["shell"]["nav"]; icon: typeof LayoutDashboard; tour?: string }> = [
  { href: "/app", label: "overview", icon: LayoutDashboard },
  { href: "/app/leads", label: "leads", icon: Target, tour: "nav-leads" },
  { href: "/app/reports", label: "reports", icon: BarChart3 },
  { href: "/app/offer", label: "offers", icon: Crosshair, tour: "nav-offer" },
];

/**
 * Rail replié à 58 px : les libellés n'apparaissent qu'au dépliage, dans la
 * gouttière vide, pour que le contenu ne bouge jamais (charte : rails).
 * Le repli après clic passe par `:has(:focus-visible)`, jamais `:focus-within`.
 */
const railItem =
  "flex min-h-[36px] items-center gap-3 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-[17px] [&_svg]:shrink-0";

const railLabel =
  "-translate-x-1 opacity-0 transition-[opacity,transform] duration-150 group-hover:translate-x-0 group-hover:opacity-100 group-has-[:focus-visible]:translate-x-0 group-has-[:focus-visible]:opacity-100 max-md:hidden";

function isNavActive(href: string, pathname: string) {
  if (href.includes("#")) return false;
  if (href === "/app") return pathname === "/app";
  return pathname === href || pathname.startsWith(`${href}/`);
}

interface WorkspaceOption {
  id: string;
  name: string;
}

const MY_WORKSPACE = "self";

function WorkspaceSwitcher({ workspaces, activeWorkspaceId }: {
  workspaces: WorkspaceOption[];
  activeWorkspaceId: string | null;
}) {
  const router = useRouter();
  const t = useT();
  const [pending, setPending] = useState(false);

  async function switchWorkspace(value: string) {
    setPending(true);
    try {
      await fetch("/api/admin/workspace-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: value === MY_WORKSPACE ? null : value }),
      });
      router.refresh();
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {activeWorkspaceId && (
        <Badge className="gap-1.5 px-2.5 py-1 text-[12px] max-md:hidden" variant="secondary">
          <Eye aria-hidden="true" /> {t.shell.workspace.readOnly}
        </Badge>
      )}
      <Select
        disabled={pending}
        onValueChange={switchWorkspace}
        value={activeWorkspaceId ?? MY_WORKSPACE}
      >
        <SelectTrigger aria-label={t.shell.workspace.label} className="h-[38px] max-w-[240px] gap-2 text-xs font-semibold max-md:max-w-[72px] max-md:[&_[data-slot=select-value]]:hidden">
          <Building2 aria-hidden="true" className="text-muted-foreground" />
          <SelectValue placeholder={t.shell.workspace.mine} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={MY_WORKSPACE}>{t.shell.workspace.mine}</SelectItem>
          {workspaces.map((workspace) => (
            <SelectItem key={workspace.id} value={workspace.id}>{workspace.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * La topbar tant que la coque n'est pas lue : des repères neutres à la place
 * de l'offre et du compte (après un court délai, pour ne rien faire clignoter
 * quand la réponse est rapide), ou l'échec et sa reprise. `hidden` : rien,
 * quand c'est le contenu qui porte l'erreur (session illisible).
 */
export function TopbarPending({ state }: { state: "loading" | "failed" | "hidden" }) {
  const visible = useDelayed();
  const t = useT();
  if (state === "failed") {
    return (
      <div className="mr-auto flex min-w-0 items-center gap-3 text-[12.5px] text-muted-foreground" role="alert">
        <span className="truncate">{t.shell.topbar.unavailable}</span>
        <RetryButton size="sm" variant="outline" />
      </div>
    );
  }
  if (state === "hidden" || !visible) return null;
  return (
    <>
      <div aria-hidden="true" className="mr-auto flex items-center gap-3">
        <Placeholder className="h-[38px] w-[200px] max-md:w-[150px]" />
      </div>
      <Placeholder className="h-[38px] w-[150px] max-md:w-[38px]" />
      <div aria-hidden="true" className="flex items-center gap-2.5 max-md:hidden">
        <Placeholder className="size-[30px] rounded-full" />
        <span className="grid gap-1.5">
          <Placeholder className="h-3 w-24" />
          <Placeholder className="h-3 w-32" />
        </span>
      </div>
    </>
  );
}

/**
 * Au plan Gratuit, le chemin vers le paiement depuis n'importe quel écran :
 * sans lui, il n'apparaissait qu'après un refus lié au plan, et une cliente
 * prête à payer ne trouvait pas où. Bouton secondaire : l'action principale
 * de chaque page garde l'accent.
 */
function PlanUpgrade({ plan }: { plan: { id: PlanId; name: string } | null }) {
  const t = useT();
  if (plan?.id !== "free") return null;
  return (
    <div className="flex items-center gap-2.5 md:border-r md:pr-3">
      <span className="text-[12.5px] text-muted-foreground max-md:hidden">{t.shell.plan.current(t.shell.plan.names[plan.id])}</span>
      <Link className={buttonVariants({ size: "sm", variant: "outline" })} href="/app/settings#abonnement">
        <span className="max-md:hidden">{t.shell.plan.upgrade}</span>
        <span className="md:hidden">{t.shell.plan.upgradeShort}</span>
      </Link>
    </div>
  );
}

export function AppShell({
  children,
  email,
  name,
  isAdmin = false,
  workspaces = [],
  activeWorkspaceId = null,
  offers = [],
  activeOfferId = null,
  runningPass = null,
  unopenedLeads = 0,
  plan = null,
  pending,
}: {
  children: React.ReactNode;
  email: string;
  name: string;
  isAdmin?: boolean;
  workspaces?: WorkspaceOption[];
  activeWorkspaceId?: string | null;
  offers?: OfferSummary[];
  activeOfferId?: string | null;
  /** Passage en cours du compte, suivi depuis n'importe quel écran. */
  runningPass?: RunningPassView | null;
  /** Leads jamais ouverts de l'offre consultée, comptés dans le titre de l'onglet. */
  unopenedLeads?: number;
  /** Plan en vigueur du compte ; absent en consultation d'un espace client. */
  plan?: { id: PlanId; name: string } | null;
  /**
   * Coque pas encore lue : ce que la topbar montre à la place de l'offre et du
   * compte (`TopbarPending`). Le rail, lui, ne dépend de rien et sert déjà.
   */
  pending?: React.ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const t = useT();

  useEffect(() => {
    setTabStatus({ unread: unopenedLeads });
    return () => setTabStatus({ unread: 0 });
  }, [unopenedLeads]);

  async function signOut() {
    await endSession().catch(() => {});
    // Sans rechargement de page : une enquête encore en cours ne s'annonce pas au compte suivant.
    resetLeadTasks();
    router.replace("/");
    router.refresh();
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-h-screen bg-background pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0 md:pl-[58px]">
        <aside
          className={cn(
            "group fixed inset-y-0 left-0 z-50 flex w-[58px] flex-col overflow-hidden border-r bg-background px-2.5 py-3.5",
            "transition-[width,box-shadow] duration-200 hover:w-[222px] hover:shadow-[0_18px_50px_rgba(20,20,30,0.13)]",
            "has-[:focus-visible]:w-[222px] has-[:focus-visible]:shadow-[0_18px_50px_rgba(20,20,30,0.13)]",
            "max-md:inset-x-0 max-md:inset-y-auto max-md:bottom-0 max-md:h-[calc(4rem+env(safe-area-inset-bottom))] max-md:w-full max-md:flex-row max-md:items-center max-md:justify-between max-md:gap-1 max-md:border-t max-md:border-r-0 max-md:px-2.5 max-md:pt-0 max-md:pb-[env(safe-area-inset-bottom)]",
            "max-md:hover:w-full max-md:hover:shadow-none max-md:has-[:focus-visible]:w-full max-md:has-[:focus-visible]:shadow-none",
          )}
        >
          <Link
            className="mt-1 mb-5 flex min-h-[38px] items-center gap-3 pl-1 text-foreground max-md:hidden"
            href="/app"
            aria-label={t.shell.nav.home}
          >
            <img src="/brand/Logo.svg" alt="" width={38} height={38} className="size-[30px] shrink-0 object-contain" />
            <span className={cn(railLabel, "text-[13.5px] font-semibold tracking-tight")}>Hune</span>
          </Link>

          <nav aria-label={t.shell.nav.label} className="grid gap-[3px] max-md:flex max-md:gap-1">
            {navigation.map(({ href, label, icon: Icon, tour }) => (
              <Link
                className={cn(railItem, isNavActive(href, pathname) && "bg-accent text-foreground hover:bg-accent")}
                data-tour={tour}
                href={href}
                key={href}
                title={t.shell.nav[label]}
              >
                <Icon aria-hidden="true" />
                <span className={railLabel}>{t.shell.nav[label]}</span>
              </Link>
            ))}
          </nav>

          <div className="mt-auto grid gap-[3px] border-t pt-2.5 max-md:mt-0 max-md:flex max-md:gap-1 max-md:border-0 max-md:pt-0">
            {isAdmin && (
              <a className={railItem} href={`${ADMIN_URL}/admin`} title={t.shell.nav.admin}>
                <ShieldCheck aria-hidden="true" />
                <span className={railLabel}>{t.shell.nav.admin}</span>
              </a>
            )}
            <Link
              className={cn(railItem, isNavActive("/app/settings", pathname) && "bg-accent text-foreground hover:bg-accent")}
              href="/app/settings"
              title={t.shell.nav.settings}
            >
              <Settings aria-hidden="true" />
              <span className={railLabel}>{t.shell.nav.settings}</span>
            </Link>
            <button className={cn(railItem, "cursor-pointer")} type="button" onClick={signOut} title={t.shell.nav.signOut}>
              <LogOut aria-hidden="true" />
              <span className={railLabel}>{t.shell.nav.signOut}</span>
            </button>
          </div>
        </aside>

        <div className="min-w-0">
          <header className="flex h-[54px] items-center justify-end gap-3 border-b bg-background px-6 max-md:h-[58px] max-md:gap-2 max-md:px-4">
            {pending ?? (
              <>
                <div className="mr-auto flex min-w-0 items-center gap-3 max-md:gap-2">
                  <OfferSwitcher activeOfferId={activeOfferId} offers={offers} />
                  <RunIndicator pass={runningPass} />
                  {isAdmin && <WorkspaceSwitcher activeWorkspaceId={activeWorkspaceId} workspaces={workspaces} />}
                </div>
                <CommandPalette activeOfferId={activeOfferId} offers={offers} />
                <PlanUpgrade plan={plan} />
                <div className="flex items-center gap-2.5 max-md:hidden">
                  <Avatar className="size-[30px]">
                    <AvatarFallback className="bg-secondary text-[12px] font-medium text-foreground">
                      {(name || email).charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <p className="grid gap-px max-md:hidden">
                    <strong className="text-[12.5px] font-medium">{name || t.shell.topbar.account}</strong>
                    <small className="text-[12px] text-muted-foreground">{email}</small>
                  </p>
                </div>
              </>
            )}
          </header>
          <main className="mx-auto w-[min(1080px,calc(100%-372px))] py-[52px] pb-25 max-[1500px]:w-[min(1080px,calc(100%-220px))] max-[1180px]:w-[calc(100%-90px)] max-md:w-[min(100%-32px,1180px)] max-md:pt-7 max-md:pb-12">
            {children}
          </main>
        </div>
      </div>
      {/* La visite de la première session ; jamais dans l'espace d'un client consulté par un admin. */}
      {!pending && <ProductTour disabled={Boolean(activeWorkspaceId)} runId={runningPass?.runId ?? null} />}
    </TooltipProvider>
  );
}
